/**
 * PreviewPanel - 左侧预览区
 * 根据当前步骤显示不同的预览内容
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useCreationStore, CharacterData, StoryData, VisualData } from '../../../store/useCreationStore';
import { User, BookOpen, Palette, Sparkles } from 'lucide-react';

const stylePreviews: Record<string, { name: string; color: string; emoji: string }> = {
  beatrix_potter: { name: '温馨水彩', color: 'from-rose-100 to-amber-50', emoji: '🎨' },
  pixar: { name: '鲜艳立体', color: 'from-blue-100 to-purple-100', emoji: '🎬' },
  disney: { name: '梦幻童话', color: 'from-pink-100 to-violet-100', emoji: '🏰' },
  sketch: { name: '铅笔素描', color: 'from-gray-100 to-slate-200', emoji: '✏️' },
};

function getStylePreview(key: string) {
  return stylePreviews[key] || { name: key, color: 'from-slate-100 to-gray-200', emoji: '✨' };
}

const themeEmojis: Record<string, string> = {
  friendship: '🤝',
  bravery: '🦁',
  family: '🏠',
  nature: '🌲',
  dream: '⭐',
  magic: '🔮',
};

export default function PreviewPanel() {
  const { step, character, story, visual } = useCreationStore();

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      {/* 预览区标题 */}
      <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          实时预览
        </h3>
      </div>

      {/* 预览内容 */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <Step1Preview key="step1" character={character} />
          )}
          {step === 2 && (
            <Step2Preview key="step2" character={character} story={story} />
          )}
          {step === 3 && (
            <Step3Preview key="step3" character={character} story={story} visual={visual} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Step 1: 角色预览
function Step1Preview({ character }: { character: CharacterData }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="text-center"
    >
      {/* 角色照片 */}
      <div className="flex justify-center mb-8">
        <div className="relative w-40 h-40">
          {(character.photoUrl || character.photoPreview) ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative w-full h-full"
            >
              <img
                src={character.photoUrl || character.photoPreview}
                alt="角色预览"
                className="w-full h-full rounded-full object-cover border-4 border-indigo-100 shadow-lg"
              />
            </motion.div>
          ) : (
            <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border-4 border-dashed border-slate-300 flex items-center justify-center">
              <User className="w-16 h-16 text-slate-300" />
            </div>
          )}
          
          {/* 装饰光环 */}
          {(character.photoUrl || character.photoPreview) && (
            <div className="absolute -inset-2 rounded-full border-2 border-indigo-200/50 animate-pulse" />
          )}
        </div>
      </div>

      {/* 角色信息 */}
      <div className="space-y-3">
        <h4 className="text-xl font-bold text-slate-800">
          {character.name || '未命名角色'}
        </h4>
        
        {character.traits.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2">
            {character.traits.map((trait: string) => (
              <span
                key={trait}
                className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-medium"
              >
                {trait}
              </span>
            ))}
          </div>
        )}

        {character.appearance && (
          <p className="text-sm text-slate-500 line-clamp-3">
            {character.appearance}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// Step 2: 故事蓝图预览
function Step2Preview({ character, story }: { character: CharacterData; story: StoryData }) {
  const ageLabels: Record<string, string> = {
    '3-4': '3-4岁 (AA级)',
    '5-6': '5-6岁 (A-B级)',
    '7-8': '7-8岁 (C-D级)',
  };

  const themeLabels: Record<string, string> = {
    friendship: '友谊与冒险',
    bravery: '勇气与成长',
    family: '家庭与爱',
    nature: '自然与探索',
    dream: '梦想与未来',
    magic: '魔法与奇幻',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* 角色小卡片 */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
        {(character.photoUrl || character.photoPreview) ? (
          <img
            src={character.photoUrl || character.photoPreview}
            alt={character.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
            <User className="w-6 h-6 text-slate-400" />
          </div>
        )}
        <div>
          <p className="font-medium text-slate-800">{character.name || '主角'}</p>
          <p className="text-xs text-slate-500">故事主角</p>
        </div>
      </div>

      {/* 配置摘要 */}
      <div className="space-y-3">
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-500 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            目标年龄
          </span>
          <span className="font-medium text-slate-800">
            {ageLabels[story.age] || story.age}
          </span>
        </div>

        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-slate-500 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            绘本页数
          </span>
          <span className="font-medium text-slate-800">{story.pages} 页</span>
        </div>

        <div className="py-2">
          <span className="text-slate-500 flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" />
            故事主题
          </span>
          {story.theme ? (
            <div className="flex items-center gap-2 mt-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <span className="text-2xl">{themeEmojis[story.theme] || '📖'}</span>
              <span className="font-medium text-slate-800">
                {themeLabels[story.theme] || story.theme}
              </span>
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">未选择主题</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Step 3: 视觉风格预览
function Step3Preview({
  character,
  story,
  visual,
}: {
  character: CharacterData;
  story: StoryData;
  visual: VisualData;
}) {
  const styleInfo = getStylePreview(visual.style);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* 绘本封面模拟 */}
      <div className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br ${styleInfo.color} shadow-lg`}>
        {/* 封面装饰 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          <motion.div
            key={visual.style}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-6xl mb-4"
          >
            {styleInfo.emoji}
          </motion.div>
          
          <h3 className="text-lg font-bold text-slate-800 mb-2">
            {character.name || '主角'}的冒险
          </h3>
          
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>{story.pages}页</span>
            <span>·</span>
            <span>{styleInfo.name}</span>
          </div>
        </div>

        {/* 角色小头像 */}
        {(character.photoUrl || character.photoPreview) && (
          <div className="absolute bottom-4 right-4">
            <img
              src={character.photoUrl || character.photoPreview}
              alt={character.name}
              className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
            />
          </div>
        )}

        {/* 风格标签 */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-white/80 backdrop-blur text-xs font-medium text-slate-700 rounded-full">
            {'GLM-5V'}
          </span>
        </div>
      </div>

      {/* 风格说明 */}
      <div className="p-4 bg-slate-50 rounded-xl">
        <h5 className="font-medium text-slate-800 mb-1">{styleInfo.name}</h5>
        <p className="text-sm text-slate-500">
          绘本将以{styleInfo.name}风格呈现，适合{story.age}岁读者
        </p>
      </div>
    </motion.div>
  );
}
